import React from 'react';
import { IModuleSchema, moduleSchema, ModuleService } from '@/api/module';
import { Button, Dialog, Modal } from 'alurkerja-ui';
import { FaPlus } from 'react-icons/fa';
import { FormContainer, FormFooter } from '@/components/form/FormContainer';
import { InputField } from '@/components/form/fields/InputField';
import { useForm } from 'react-hook-form';
import { OutlineButton } from '@/components/shared/button/OutlineButton';
import { zodResolver } from '@hookform/resolvers/zod';
import { LinkButton } from '@/components/shared/button/LinkButton';
import { SelectField } from '@/components/form/fields/SelectField';
import { IUserSchema, UserService } from '@/api/user';

interface CreateSubmoduleProps {
  onSuccess: () => void;
  module: IModuleSchema;
}

interface ModalContentProps extends CreateSubmoduleProps {
  closeModal: () => void;
}

const ModalContent: React.FC<ModalContentProps> = ({
  module,
  onSuccess,
  closeModal,
}) => {
  const form = useForm<IModuleSchema>({
    resolver: zodResolver(moduleSchema),
  });

  const { data: usersData } = UserService.useGetAllUser();
  const userOptions =
    usersData?.data?.content?.map((user: IUserSchema) => ({
      label: `${user.first_name} ${user.last_name}`,
      value: user.id,
    })) ?? [];

  const { mutate, isPending: isLoading } = ModuleService.useCreate();

  const submitAction = (data: IModuleSchema) => {
    data.parent_id = module.id;
    data.project_id = module.project_id;

    mutate(data, {
      onSuccess: () => {
        Dialog.success({
          title: 'Success',
          description: 'Submodule has been created',
          callback: onSuccess,
        });
      },
      onError: () => {
        Dialog.error({
          title: 'Error',
          description: 'Failed to create submodule',
        });
      },
    });
  };

  return (
    <FormContainer form={form} onSubmit={submitAction}>
      <div className="flex flex-col gap-4">
        <InputField
          name="parent_module_name"
          defaultValue={module.module_name}
          label="Epic"
          disabled
        />

        <InputField name="module_name" label="User Story" />
      </div>

      <SelectField
        label="PIC"
        name="pic_id"
        options={userOptions}
        placeholder="Select PIC"
      />

      <FormFooter>
        <OutlineButton onClick={closeModal}>Cancel</OutlineButton>

        <Button loading={isLoading}>Save</Button>
      </FormFooter>
    </FormContainer>
  );
};

export const CreateSubmodule: React.FC<CreateSubmoduleProps> = props => {
  return (
    <Modal
      title="Create User Story"
      triggerButton={
        <LinkButton icon={<FaPlus className="text-blue-500" />} />
      }
    >
      {({ closeModal }) => {
        return (
          <div className="p-4">
            <ModalContent {...props} closeModal={closeModal} />
          </div>
        );
      }}
    </Modal>
  );
};
